import React, { useRef, useState, useMemo } from 'react';
import { Box, Typography, Button, useTheme } from '@mui/material';
import clsx from 'clsx';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';

import {
  getCategoryIcon,
  ROOT_CATEGORY_KEYS,
} from '../../../configuration/categoryConfigIcon';
import {
  CATEGORY_CARD_BASE_SX,
  SUB_CATEGORY_CARD_BASE_SX,
  BACK_BUTTON_BASE_SX,
} from '../../../configuration/categoryStyles';

interface Subcategory {
  key: string;
  name: string;
}

interface Category {
  key: string;
  name: string;
  subcategories?: Subcategory[];
}

interface Props {
  categories: Category[];
}

const CollapsibleCategorySection: React.FC<Props> = ({ categories }) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const categoriesRef = useRef<HTMLDivElement | null>(null);

  // smoothed scroll to top of chosen category
  const HEADER_OFFSET = 105;

  const scrollToCategories = () => {
    if (!categoriesRef.current) return;

    const rect = categoriesRef.current.getBoundingClientRect();
    const absoluteY = rect.top + window.scrollY;

    window.scrollTo({
      top: absoluteY - HEADER_OFFSET,
      behavior: 'smooth',
    });
  };

  const handleCategoryClick = (categoryKey: string) => {
    if (expandedCategory === categoryKey) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryKey);
    }
    scrollToCategories();
  };

  const handleSubcategoryClick = (
    categoryKey: string,
    subcategoryKey: string
  ) => {
    navigate(
      `/browse-handyman?selectedCategories=${categoryKey},${subcategoryKey}`
    );
  };

  const rootCategories = useMemo(() => {
    const filtered = categories.filter((cat) =>
      ROOT_CATEGORY_KEYS.includes(cat.key)
    );
    filtered.sort(
      (a, b) =>
        ROOT_CATEGORY_KEYS.indexOf(a.key) - ROOT_CATEGORY_KEYS.indexOf(b.key)
    );
    return filtered;
  }, [categories]);

  const dynamicStyles = {
    containerBackground: isLight ? '#E5E5DC' : '#1A1A1A',
    textColor: isLight ? '#393939' : '#B6B6B6',
    backButtonTextColor: isLight ? '#393939' : 'rgba(182, 182, 182, 1)',
  };

  return (
    <Box
      ref={categoriesRef}
      className='categories-blocks'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '956px',
      }}
    >
      {/* Grid Container for Category Cards */}
      <Box
        className={clsx('categories-block', {
          'categories-block--expanded': !expandedCategory,
          'categories-block--collapsed': expandedCategory,
        })}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: { xs: '16px', sm: '32px' },
          width: '100%',
        }}
      >
        {rootCategories.map((category) => (
          <Box
            key={category.key}
            onClick={() => handleCategoryClick(category.key)}
            sx={{
              ...CATEGORY_CARD_BASE_SX,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              justifySelf: 'center',
              background: dynamicStyles.containerBackground,
              width: { xs: '192px', sm: '462px' },
              padding: '24px',
              gap: '12px',
              aspectRatio: '1',
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: { xs: '64px', sm: '160px' },
                height: { xs: '64px', sm: '160px' },
                padding: { xs: '2px', sm: '15px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {getCategoryIcon(category.key)}
            </Box>

            {/* Category Name */}
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontWeight: { xs: 900, sm: 700 },
                textTransform: { xs: 'uppercase', sm: 'unset' },
                fontSize: { xs: '12px', sm: '24px' },
                lineHeight: '133%',
                textAlign: 'center',
                color: dynamicStyles.textColor,
              }}
            >
              {category.name}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Expanded subcategories section - shown below the grid */}
      <Box
        className={clsx('categories-block', {
          'categories-block--expanded': expandedCategory,
          'categories-block--collapsed': !expandedCategory,
        })}
        sx={{
          width: '100%',
        }}
      >
        {rootCategories
          .filter((cat) => cat.key === expandedCategory)
          .map((category) => (
            <Box
              key={category.key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: { xs: '16px', sm: '24px' },
                background: dynamicStyles.containerBackground,
                borderRadius: '64px',
                boxShadow: CATEGORY_CARD_BASE_SX.boxShadow,
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '0 35px',
                    marginBottom: '24px',
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      padding: { sm: '5px' },
                      width: { xs: '48px', sm: '64px' },
                      height: { xs: '48px', sm: '64px' },
                    }}
                  >
                    {getCategoryIcon(category.key)}
                  </Box>
                  {/* Category Name */}
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontWeight: 700,
                      fontSize: { xs: '20px', sm: '24px' },
                      lineHeight: '133%',
                      textAlign: 'center',
                      color: dynamicStyles.textColor,
                    }}
                  >
                    {category.name}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(auto-fit, minmax(180px, 1fr))',
                      sm: 'repeat(auto-fit, minmax(292px, 1fr))',
                    },
                    gap: { xs: '8px', sm: '16px' },
                    width: '100%',
                    marginBottom: { xs: '16px', sm: '24px' },
                  }}
                >
                  {category.subcategories?.map((subcategory) => (
                    <Box
                      key={subcategory.key}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubcategoryClick(category.key, subcategory.key);
                      }}
                      className='category-title'
                      sx={{
                        ...SUB_CATEGORY_CARD_BASE_SX,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: dynamicStyles.containerBackground,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          height: { xs: '48px', sm: '80px' },
                          width: { xs: '48px', sm: '80px' },
                        }}
                      >
                        {getCategoryIcon(subcategory.key)}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: 'Montserrat',
                          fontWeight: { xs: 900, sm: 700 },
                          fontSize: { xs: '12px', sm: '20px' },
                          lineHeight: { xs: '166%', sm: '160%' },
                          letterSpacing: '0.15px',
                          textAlign: 'center',
                          textTransform: { xs: 'uppercase', sm: 'unset' },
                          color: dynamicStyles.textColor,
                        }}
                      >
                        {subcategory.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Button
                onClick={() => {
                  scrollToCategories();
                  setExpandedCategory(null);
                }}
                startIcon={<ArrowBackIosNewIcon />}
                sx={{
                  ...BACK_BUTTON_BASE_SX,
                  '&&': {
                    color: dynamicStyles.backButtonTextColor,
                    '&&:hover': {
                      color: 'rgba(0, 92, 173, 1)',
                    },
                  },
                }}
              >
                Back
              </Button>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default CollapsibleCategorySection;
